import React from 'react';
import { FeatureFlagName } from '@curbside-quest/shared';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

interface FeatureGateProps {
  /** The feature flag name to check */
  flag: FeatureFlagName;
  /** Content to render when the feature is disabled */
  fallback?: React.ReactNode;
  /** Content to render when the feature is enabled */
  children: React.ReactNode;
  /** Whether to show loading state while checking the flag */
  showLoading?: boolean;
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
  /** Whether to invert the flag check (render children when flag is false) */
  invert?: boolean;
}

/**
 * Component that conditionally renders children based on feature flag state
 * 
 * @example
 * ```tsx
 * <FeatureGate flag="AI_ANALYSIS_ENABLED">
 *   <AIAnalysisButton />
 * </FeatureGate>
 * 
 * <FeatureGate 
 *   flag="MAINTENANCE_MODE" 
 *   fallback={<MaintenanceMessage />}
 * >
 *   <AppContent />
 * </FeatureGate>
 * ```
 */
export function FeatureGate({
  flag,
  fallback = null,
  children,
  showLoading = false,
  loadingComponent = <div>Loading...</div>,
  invert = false
}: FeatureGateProps): JSX.Element | null {
  const { isEnabled, isLoading, error } = useFeatureFlag();

  // Show loading state if requested and still loading
  if (showLoading && isLoading) {
    return <>{loadingComponent}</>;
  }

  // If there's an error, log it but continue with default behavior
  if (error) {
    console.warn(`FeatureGate error for flag ${flag}:`, error);
  }

  const flagEnabled = isEnabled(flag);
  const shouldRender = invert ? !flagEnabled : flagEnabled;

  if (shouldRender) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

/**
 * Higher-order component version of FeatureGate for wrapping components
 */
export function withFeatureFlag<P extends object>(
  Component: React.ComponentType<P>,
  flag: FeatureFlagName,
  fallback?: React.ReactNode
) {
  const WrappedComponent = (props: P) => (
    <FeatureGate flag={flag} fallback={fallback}>
      <Component {...props} />
    </FeatureGate>
  );

  WrappedComponent.displayName = `withFeatureFlag(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}

/**
 * Component for showing content when a feature is NOT available
 */
export function FeatureNotAvailable({ 
  title = "Feature Not Available",
  message = "This feature is currently not available.",
  className = ""
}: {
  title?: string;
  message?: string;
  className?: string;
}) {
  return (
    <div className={`text-center py-8 px-4 ${className}`}>
      <div className="max-w-md mx-auto">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-600">{message}</p>
      </div>
    </div>
  );
}

/**
 * Hook for conditionally executing effects based on feature flags
 */
export function useFeatureEffect(
  flag: FeatureFlagName,
  effect: React.EffectCallback,
  deps?: React.DependencyList
) {
  const { isEnabled } = useFeatureFlag();
  
  React.useEffect(() => {
    if (isEnabled(flag)) {
      return effect();
    }
  }, [flag, isEnabled(flag), ...(deps || [])]);
}